/**
 * @file    i18n.js - Configuration file for the i18next internationalization
 *                    library. See https://www.i18next.com/ for information.
 *
 * @author  Robert Mirandola <robert@distributive.network>
 * @date    Oct. 2022
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';

import { debugging } from './debugging';

const { VITE_GIT_REV } = import.meta.env;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(HttpApi)
  .init({
    supportedLngs: ['en', 'fr', 'smith'],
    nonExplicitSupportedLngs: true, /* e.g., 'en-CA' is coerced into 'en' */
    fallbackLng: 'en', /* Fallback to English translation JSON file if locale is not correctly set */
    detection: {
      order: ['navigator'] /* Use window.navigator.language to detect language if the user does not set their locale */,
      caches: [] /* Disable storing the current language in a cookie and in localStorage */
    },
    backend: {
      /**
       * Use JSON files in the locales folder to find translation values
       * @param {string[]} languages The list of languages to load. Will contain multiple elements if
       *                             `allowMultiLoading` is true.
       *
       * @returns                    The resolved path to the translation file to load.
       */
      loadPath([language]) {
        if (language.startsWith('en'))
          return `/locales/en/translation.json?${VITE_GIT_REV}`;
        else if (language.startsWith('fr'))
          return `/locales/fr/translation.json?${VITE_GIT_REV}`
        else
          return `/locales/{{lng}}/translation.json?${VITE_GIT_REV}`;
      }
    },
    react: { useSuspense: false },
    debug: debugging('i18next'),
  });

export { i18n };
